import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const AccessManagement = ({ userProfile, onAccessRequest }) => {
  const [activeTab, setActiveTab] = useState('permissions');
  const [accessRequest, setAccessRequest] = useState({
    datasetId: '',
    accessLevel: 'standard',
    researchPurpose: '',
    institutionAffiliation: '',
    projectDuration: '6_months',
    dataUsageAgreement: false,
    gdprCompliance: false,
    publicationIntent: false
  });

  const accessLevels = [
    { 
      value: 'public', 
      label: 'Public Access',
      description: 'Anonymized datasets with maximum privacy protection',
      requirements: ['Valid email verification'],
      restrictions: ['No PII data', 'Aggregated data only', 'Attribution required']
    },
    { 
      value: 'academic', 
      label: 'Academic Research',
      description: 'Enhanced datasets for verified academic researchers',
      requirements: ['Academic institution verification', 'Research proposal', 'Ethics approval'],
      restrictions: ['Non-commercial use', 'Publication requirements', 'Data retention limits']
    },
    { 
      value: 'institutional', 
      label: 'Institutional Access',
      description: 'Full datasets for government and policy organizations',
      requirements: ['Institutional verification', 'Security clearance', 'Legal agreement'],
      restrictions: ['Classified handling', 'Need-to-know basis', 'Audit requirements']
    }
  ];

  const projectDurationOptions = [
    { value: '3_months', label: '3 months' },
    { value: '6_months', label: '6 months' },
    { value: '1_year', label: '1 year' },
    { value: '2_years', label: '2 years' },
    { value: 'ongoing', label: 'Ongoing (requires annual review)' }
  ];

  const currentPermissions = [
    {
      dataset: 'EU Cross-Border Narrative Analysis 2024',
      accessLevel: 'academic',
      grantedDate: '2024-08-15',
      expiryDate: '2025-02-15',
      status: 'active',
      usageQuota: { used: 1247, limit: 10000 },
      restrictions: ['Non-commercial use', 'Attribution required']
    },
    {
      dataset: 'Platform Signals Aggregation 2024',
      accessLevel: 'public',
      grantedDate: '2024-09-01',
      expiryDate: '2025-03-01',
      status: 'active',
      usageQuota: { used: 456, limit: 5000 },
      restrictions: ['Aggregated data only', 'No re-identification attempts']
    },
    {
      dataset: 'FIMI Threat Intelligence Q3 2024',
      accessLevel: 'institutional',
      grantedDate: '2024-09-10',
      expiryDate: '2024-12-10',
      status: 'pending_approval',
      usageQuota: { used: 0, limit: 50000 },
      restrictions: ['Classified handling', 'Security clearance required']
    }
  ];

  const auditLogs = [
    {
      timestamp: '2024-09-21T10:15:00Z',
      action: 'Dataset Access',
      dataset: 'EU Cross-Border Narrative Analysis 2024',
      details: 'Query executed: Temporal trend analysis',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Research Browser)',
      status: 'success'
    },
    {
      timestamp: '2024-09-21T09:45:00Z',
      action: 'Data Export',
      dataset: 'Platform Signals Aggregation 2024',
      details: 'CSV export: 2,456 records',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Research Browser)',
      status: 'success'
    },
    {
      timestamp: '2024-09-20T16:30:00Z',
      action: 'Access Request',
      dataset: 'FIMI Threat Intelligence Q3 2024',
      details: 'Institutional access request submitted',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Research Browser)',
      status: 'pending'
    },
    {
      timestamp: '2024-09-20T14:22:00Z',
      action: 'Query Execution',
      dataset: 'EU Cross-Border Narrative Analysis 2024',
      details: 'Failed query: Insufficient permissions for raw data',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Research Browser)',
      status: 'error'
    }
  ];

  const handleAccessRequest = () => {
    if (!accessRequest?.dataUsageAgreement || !accessRequest?.gdprCompliance) {
      return;
    }
    
    onAccessRequest(accessRequest);
    
    // Reset form
    setAccessRequest({
      datasetId: '',
      accessLevel: 'standard',
      researchPurpose: '',
      institutionAffiliation: '',
      projectDuration: '6_months',
      dataUsageAgreement: false,
      gdprCompliance: false,
      publicationIntent: false
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-success bg-success/10';
      case 'pending_approval':
        return 'text-warning bg-warning/10';
      case 'expired':
        return 'text-destructive bg-destructive/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Access Management</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Manage data access permissions and compliance requirements
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Shield" size={16} className="text-success" />
            <span className="text-xs font-medium text-success">DSA Compliant</span>
          </div>
        </div>

        <div className="flex space-x-1">
          {[
            { id: 'permissions', label: 'Current Permissions', icon: 'Key' },
            { id: 'request', label: 'Request Access', icon: 'Plus' },
            { id: 'audit', label: 'Audit Log', icon: 'FileText' }
          ]?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`
                flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth
                ${activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground shadow-soft'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }
              `}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="p-6">
        {activeTab === 'permissions' && (
          <div className="space-y-4">
            {currentPermissions?.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="Key" size={48} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No Active Permissions</h3>
                <p className="text-muted-foreground">
                  Request access to datasets to begin your research
                </p>
              </div>
            ) : (
              currentPermissions?.map((permission, index) => (
                <div key={index} className="p-4 border border-border rounded-lg">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-foreground mb-1">
                        {permission?.dataset}
                      </h3>
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <span>Access Level: {permission?.accessLevel}</span>
                        <span>Granted: {formatDate(permission?.grantedDate)}</span>
                        <span>Expires: {formatDate(permission?.expiryDate)}</span>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(permission?.status)}`}>
                      {permission?.status?.replace('_', ' ')?.toUpperCase()}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-sm font-medium text-foreground mb-2">Usage Quota</div>
                      <div className="flex items-center space-x-2">
                        <div className="flex-1 bg-muted rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-smooth"
                            style={{
                              width: `${(permission?.usageQuota?.used / permission?.usageQuota?.limit) * 100}%`
                            }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Intl.NumberFormat('en-EU')?.format(permission?.usageQuota?.used)} / {new Intl.NumberFormat('en-EU')?.format(permission?.usageQuota?.limit)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground mb-2">Restrictions</div>
                      <div className="space-y-1">
                        {permission?.restrictions?.map((restriction, idx) => (
                          <div key={idx} className="flex items-center space-x-2">
                            <Icon name="AlertCircle" size={12} className="text-warning" />
                            <span className="text-xs text-muted-foreground">{restriction}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Icon name="Calendar" size={14} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {Math.ceil((new Date(permission.expiryDate) - new Date()) / (1000 * 60 * 60 * 24))} days remaining
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm">
                        Extend Access
                      </Button>
                      <Button variant="ghost" size="sm" iconName="Download">
                        Export Data
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'request' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Dataset ID"
                placeholder="Enter dataset identifier"
                value={accessRequest?.datasetId}
                onChange={(e) => setAccessRequest(prev => ({ ...prev, datasetId: e?.target?.value }))}
                required
              />
              <Select
                label="Access Level"
                options={accessLevels?.map(level => ({ value: level?.value, label: level?.label }))}
                value={accessRequest?.accessLevel}
                onChange={(value) => setAccessRequest(prev => ({ ...prev, accessLevel: value }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Research Purpose *
              </label>
              <textarea
                value={accessRequest?.researchPurpose}
                onChange={(e) => setAccessRequest(prev => ({ ...prev, researchPurpose: e?.target?.value }))}
                placeholder="Describe your research objectives, methodology, and expected outcomes..."
                className="w-full h-32 p-3 border border-border rounded-lg bg-background text-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Institution Affiliation"
                placeholder="University, research institute, or organization"
                value={accessRequest?.institutionAffiliation}
                onChange={(e) => setAccessRequest(prev => ({ ...prev, institutionAffiliation: e?.target?.value }))}
                required
              />
              <Select
                label="Project Duration"
                options={projectDurationOptions}
                value={accessRequest?.projectDuration}
                onChange={(value) => setAccessRequest(prev => ({ ...prev, projectDuration: value }))}
              />
            </div>

            {/* Access Level Information */}
            {accessRequest?.accessLevel && (
              <div className="p-4 bg-muted/50 rounded-lg">
                {accessLevels?.find(level => level?.value === accessRequest?.accessLevel) && (
                  <div>
                    <h3 className="text-sm font-medium text-foreground mb-2">
                      {accessLevels?.find(level => level?.value === accessRequest?.accessLevel)?.label} Requirements
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      {accessLevels?.find(level => level?.value === accessRequest?.accessLevel)?.description}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-medium text-foreground mb-2">Requirements:</div>
                        <ul className="space-y-1">
                          {accessLevels?.find(level => level?.value === accessRequest?.accessLevel)?.requirements?.map((req, idx) => (
                            <li key={idx} className="flex items-center space-x-2">
                              <Icon name="Check" size={12} className="text-success" />
                              <span className="text-xs text-muted-foreground">{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <div className="text-xs font-medium text-foreground mb-2">Restrictions:</div>
                        <ul className="space-y-1">
                          {accessLevels?.find(level => level?.value === accessRequest?.accessLevel)?.restrictions?.map((restriction, idx) => (
                            <li key={idx} className="flex items-center space-x-2">
                              <Icon name="AlertCircle" size={12} className="text-warning" />
                              <span className="text-xs text-muted-foreground">{restriction}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-4">
              <Checkbox
                label="I agree to the Data Usage Agreement and will comply with all terms and conditions"
                checked={accessRequest?.dataUsageAgreement}
                onChange={(e) => setAccessRequest(prev => ({ ...prev, dataUsageAgreement: e?.target?.checked }))}
                required
              />
              <Checkbox
                label="I confirm GDPR compliance and will implement appropriate data protection measures"
                checked={accessRequest?.gdprCompliance}
                onChange={(e) => setAccessRequest(prev => ({ ...prev, gdprCompliance: e?.target?.checked }))}
                required
              />
              <Checkbox
                label="I intend to publish research findings and will provide proper attribution"
                checked={accessRequest?.publicationIntent}
                onChange={(e) => setAccessRequest(prev => ({ ...prev, publicationIntent: e?.target?.checked }))}
              />
            </div>

            <div className="flex items-center justify-between pt-6 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Access requests are typically processed within 3-5 business days
              </div>
              <Button
                onClick={handleAccessRequest}
                disabled={!accessRequest?.dataUsageAgreement || !accessRequest?.gdprCompliance || !accessRequest?.researchPurpose?.trim()}
                iconName="Send"
                iconPosition="left"
              >
                Submit Request
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-muted-foreground">
                Showing recent activity for compliance tracking
              </div>
              <Button variant="outline" size="sm" iconName="Download">
                Export Audit Log
              </Button>
            </div>

            {auditLogs?.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No Audit Records</h3>
                <p className="text-muted-foreground">
                  Your data access activities will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {auditLogs?.map((log, index) => (
                  <div key={index} className="p-4 border border-border rounded-lg hover:bg-muted/20 transition-smooth">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className={`w-2 h-2 rounded-full ${
                          log?.status === 'success' ? 'bg-success' :
                          log?.status === 'pending' ? 'bg-warning' :
                          log?.status === 'error' ? 'bg-destructive' : 'bg-muted-foreground'
                        }`} />
                        <div>
                          <div className="text-sm font-medium text-foreground">{log?.action}</div>
                          <div className="text-xs text-muted-foreground">{log?.dataset}</div>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatTimestamp(log?.timestamp)}
                      </div>
                    </div>
                    <div className="ml-5 text-sm text-muted-foreground mb-2">
                      {log?.details}
                    </div>
                    <div className="ml-5 flex items-center space-x-4 text-xs text-muted-foreground">
                      <span>IP: {log?.ipAddress}</span>
                      <span className={`px-2 py-1 rounded-full ${getStatusColor(log?.status)}`}>
                        {log?.status?.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessManagement;